import { useState } from 'react';
import { Dispatch, SetStateAction } from 'react';

import classNames from 'classnames';

import { useAiAssistantStatusQuery } from '@query';

import * as Dialog from '@radix-ui/react-dialog';
import { CrossLg } from '@ui/uikit/components/icons/mono/CrossLg';
import { Wallet } from '@ui-uikit/lib/components/icons/mono';
import { Pen } from '@ui-uikit/lib/components/icons/mono';
import { MagicAi } from '@ui-uikit/lib/components/icons/poly/MagicAi';

import { useNavLayout } from '../NavLayout';
import Chat from './Chat';
import Creadits from './Credits';

type ActiveTabPossible = 'chat' | 'credits';

const ChatContent = ({
  activeTab,
  setActiveTab,
}: {
  activeTab: ActiveTabPossible;
  setActiveTab: Dispatch<SetStateAction<ActiveTabPossible>>;
}) => {
  const { user, onLogin } = useNavLayout();

  const { data, isPending } = useAiAssistantStatusQuery({ enabled: !!user });

  if (!user) {
    return (
      <div className="w-full flex flex-col justify-center items-center gap-6 text-center h-full">
        <span className="msq-btn msq-btn-icon-lg msq-btn-brand-gradient rounded-full">
          <MagicAi className="icon-xs" />
        </span>
        <span className="text-caption-md-a">
          Hello! I'm Mystic, your AI assistant <br />
          for navigating the Web3 world.
        </span>
        <span className="text-caption-md-a">Connect your wallet to get started.</span>
        <button className="msq-btn msq-btn-lg msq-btn-primary" onClick={onLogin}>
          Connect
        </button>
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="flex justify-center items-center h-full">
        <h2 className="text-h2-a">Loading...</h2>
      </div>
    );
  }

  if (!isPending && !data?.isAvailable) {
    return (
      <div className="flex justify-center items-center h-full">
        <h2 className="text-h2-a">Chat is not avalible</h2>
      </div>
    );
  }

  if (activeTab === 'chat') {
    return <Chat />;
  }

  if (activeTab === 'credits') {
    return <Creadits backToChat={() => setActiveTab('chat')} isFocused={activeTab === 'credits'} />;
  }
};

const ChatDialog = ({ ...props }: Dialog.DialogProps) => {
  const [activeTab, setActiveTab] = useState<ActiveTabPossible>('chat');

  return (
    <Dialog.Root {...props}>
      <Dialog.Portal>
        <Dialog.Overlay
          forceMount
          className="msq-dialog msq-dialog-animate msq-dialog-mobile-menu-m xl-msq:msq-dialog-mobile-menu-d bg-transparent"
        >
          <Dialog.Content
            forceMount
            aria-describedby={undefined}
            className="msq-dialog-content sm-msq:msq-dialog-content-sm sm-msq:msq-dialog-content-right xl-msq:max-w-[600px] z-50 border border-base-border-light block h-full"
          >
            <div className="grid grid-cols-[1fr_50px] h-full">
              <div className="border-r w-full border-base-border-light h-full overflow-hidden">
                <ChatContent activeTab={activeTab} setActiveTab={setActiveTab} />
              </div>
              <div className="flex flex-col p-2 gap-2">
                <Dialog.Close asChild>
                  <button className="msq-btn msq-btn-icon-md msq-dialog-icon-button mb-2">
                    <CrossLg className="msq-btn msq-btn-icon-child" />
                  </button>
                </Dialog.Close>
                <div
                  className="msq-btn msq-btn-icon-md msq-dialog-icon-button bg-base-bg-tertiary"
                  onClick={() => setActiveTab('chat')}
                >
                  <Pen
                    className={classNames('icon-xs', {
                      'text-base-text-primary': activeTab === 'chat',
                      'text-base-text-tertiary': activeTab !== 'chat',
                    })}
                  />
                </div>
                <div
                  className="msq-btn msq-btn-icon-md msq-dialog-icon-button bg-base-bg-tertiary"
                  onClick={() => setActiveTab('credits')}
                >
                  <Wallet
                    className={classNames('icon-xs', {
                      'text-base-text-primary': activeTab === 'credits',
                      'text-base-text-tertiary': activeTab !== 'credits',
                    })}
                  />
                </div>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ChatDialog;
